import { useState, useEffect, useMemo } from "react";
import ApexCharts from "react-apexcharts";
import { Link } from "react-router-dom";
import api from "../config/URL";
import { toast } from "react-toastify";
import TeacherDashboard from "./TeacherDashboard";
import { useNavigate } from "react-router-dom";

/* ===== TOP CARD ===== */
function TopStatCard({ title, value, link, state }) {
  return (
    <div className="col-md-3 mb-3">
      <Link to={link} state={state} style={{ textDecoration: "none" }}>
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body">
            <h6 className="text-secondary">{title}</h6>
            <h4 className="fw-bold text-dark">{value ?? 0}</h4>
          </div>
        </div>
      </Link>
    </div>
  );
}

function LeadDashboard({ selectedCenter }) {
  const navigate = useNavigate();

  const roleKey = localStorage.getItem("userName");
  const teacherId = localStorage.getItem("userId");
  const centerLocalId = localStorage.getItem("selectedCenterId");
  const isTeacher = roleKey === "SMS_TEACHER";
  const isAdmin = roleKey === "SMS_ADMIN";
  const [selectedDate, setSelectedDate] = useState(null);

  const [loading, setLoading] = useState(true);
  const [datas, setDatas] = useState({
    leads: {},
    enrollment: [],
    overallEnrollment: {},
    levelCount: [],
    revenue: [],
    teacherDashboard: {},
  });

  const fontFamily =
    "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial";

  /* ===== API CALL ===== */
  const getData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("roleKey", roleKey);
      params.append(
        "centerId",
        centerLocalId && centerLocalId !== "undefined"
          ? centerLocalId
          : selectedCenter || 0,
      );

      if (isTeacher && teacherId) {
        params.append("teacherId", teacherId);
      }

      // if (selectedDate) params.append("weekDAY", selectedDate);
      if (selectedDate) {
        params.append("weekDAY", selectedDate);
      }

      const res = await api.get(`getHomeDashboard?${params.toString()}`);
      setDatas(res?.data || {});
    } catch (err) {
      toast.error("Error Fetching Dashboard Data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [selectedCenter]);

  /* ===== CHART CONFIGS (HOOKS MUST ALWAYS RUN) ===== */
  const enrollmentOptions = useMemo(
    () => ({
      chart: { type: "bar", stacked: true, fontFamily },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
      xaxis: {
        categories: datas?.enrollment?.map((c) =>
          c.centerName?.replace("AL @ ", "").replace("Centre", "").trim(),
        ),
      },
      legend: { position: "bottom" },
    }),
    [datas, fontFamily],
  );

  const enrollmentSeries = [
    {
      name: "Available",
      data: datas?.enrollment?.map((c) =>
        Math.max((c.target || 0) - (c.studentCount || 0), 0),
      ),
    },
    {
      name: "Occupied",
      data: datas?.enrollment?.map((c) => c.studentCount || 0),
    },
  ];

  const levelOptions = {
    chart: {
      type: "bar",
      fontFamily,
      toolbar: { show: false },
    },
    colors: ["#6f42c1"], // ✅ purple color like image
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "35%", // ✅ controls bar width
        borderRadius: 4, // ✅ rounded corners
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => (val > 0 ? val : ""),
      offsetY: -20,
      style: {
        fontSize: "12px",
        fontWeight: "600",
        colors: ["#6f42c1"], // label color same as bar
      },
    },
    xaxis: {
      categories: datas?.levelCount?.map((l) => l.levelCode),
      labels: {
        style: {
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => Math.round(val),
      },
    },
    grid: {
      strokeDashArray: 4,
    },
  };

  const levelSeries = [
    {
      name: "Students",
      data: datas?.levelCount?.map((l) => l.studentCount || 0),
    },
  ];

  const handleRevenueClick = (event, chartContext, config) => {
    const seriesIndex = config.seriesIndex;

    if (seriesIndex === undefined) return;

    const status = seriesIndex === 0 ? "PAID" : "PARTIALLY_PAID";

    navigate("/invoice", {
      state: { paymentStatus: status },
    });
  };

  const formatAmount = (val) => {
    if (!val) return "0.00";
    return Number(val).toFixed(2);
  };

  const revenueOptions = {
    chart: {
      type: "bar",
      stacked: false,
      toolbar: { show: true },
      fontFamily,
      events: {
        dataPointSelection: handleRevenueClick,
      },
    },
    colors: ["#4286f5", "#ea4336"],

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "40%", // ⬅️ slightly slimmer bars
        borderRadius: 4,
        dataLabels: {
          position: "top",
        },
      },
    },

    dataLabels: {
      enabled: false,
    },

    xaxis: {
      categories: datas?.revenue?.map((r) => r.month),
      labels: {
        style: {
          fontSize: "12px",
        },
      },
    },

    yaxis: {
      labels: {
        formatter: (val) => formatAmount(val),
      },
    },

    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => formatAmount(val),
      },
    },

    legend: {
      position: "top",
      horizontalAlign: "right",
    },

    grid: {
      strokeDashArray: 4,
    },
  };

  const gaugeOptions = {
    chart: { type: "radialBar" },
    colors: ["#287F71"],
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loading">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  /* ===== TEACHER VIEW (RETURN AFTER HOOKS) ===== */
  if (isTeacher) {
    return (
      <TeacherDashboard
        data={datas}
        loading={loading}
        setSelectedDate={setSelectedDate}
        getData={getData}
        selectedDate={selectedDate}
      />
    );
  }

  /* ===== ADMIN / OTHER ROLES VIEW ===== */
  return (
    <section>
      <div className="container mt-4">
        <div className="row mt-3">
          <TopStatCard
            title="Leads(NEW) Received"
            value={datas?.leads?.newLeads}
            link="/lead/lead"
            state={{ leadStatus: "NEW_WAITLIST" }}
          />
          <TopStatCard
            title="Assessment Arranged"
            value={datas?.leads?.assessmentArranged}
            link="/time-slot"
          />
          <TopStatCard
            title="Assessment Done"
            value={datas?.leads?.assessmentDone}
            link="/lead/lead"
            state={{ leadStatus: "ASSESSMENT_DONE" }}
          />
          <TopStatCard
            title="Enrolled (Monthly)"
            value={datas?.leads?.enrolled}
            link="/student"
          />
        </div>

        <div className="row">
          <div className="col-md-6 mb-4">
            <div className="card border-0 p-3 shadow-sm h-100">
              <div className="d-flex">
                <h6 className="text-center">Enrollment</h6>
                <small> (Montly)</small>
              </div>

              {!loading && (
                <ApexCharts
                  options={enrollmentOptions}
                  series={enrollmentSeries}
                  type="bar"
                  height={300}
                />
              )}
            </div>
          </div>

          <div className="col-md-6 mb-4">
            <div className="card border-0 p-3 shadow-sm h-100">
              <div className="d-flex">
                <h6 className="text-center">Level Count</h6>
                <small> (Montly)</small>
              </div>
              {!loading && (
                <ApexCharts
                  options={levelOptions}
                  series={levelSeries}
                  type="bar"
                  height={300}
                />
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="col-md-8 mb-4">
              <div className="card border-0 p-3 shadow-sm h-100">
                <div className="d-flex">
                  <h6 className="text-center">Revenue</h6>
                  <small> (Montly)</small>
                </div>
                {!loading && (
                  <ApexCharts
                    options={revenueOptions}
                    series={[
                      {
                        name: "Paid",
                        data: datas?.revenue?.map((r) =>
                          Number((r.paidAmount || 0).toFixed(2)),
                        ),
                      },
                      {
                        name: "Unpaid",
                        data: datas?.revenue?.map((r) =>
                          Number((r.unpaidAmount || 0).toFixed(2)),
                        ),
                      },
                    ]}
                    type="bar"
                    height={250}
                  />
                )}
              </div>
            </div>
          )}

          <div className="col-md-4 mb-4">
            <div className="card border-0 p-3 shadow-sm h-100">
              <h6>Total Capacity</h6>
              {!loading && (
                <ApexCharts
                  options={gaugeOptions}
                  series={[datas?.overallEnrollment?.capacityPercentage || 0]}
                  type="radialBar"
                  height={250}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LeadDashboard;
